import React, { ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import {
  DashboardNavigationDrawer,
  DashboardNavigationRail,
} from "../DashboardNavigation";
import AccountNavigation from "../AccountNavigation";
import {
  DASHBOARD_NAV_COLLAPSED_WIDTH,
  DASHBOARD_NAV_EXPANDED_WIDTH,
  useDashboardNavigationPreference,
} from "../../contexts/DashboardShellContext";

export type StudioNavigationControls = {
  onOpenNavigation?: () => void;
  closeNavigation: () => void;
  navigationOpen: boolean;
};

type StudioNavigationProps = {
  children: (controls: StudioNavigationControls) => ReactNode;
};

const StudioNavigationContext = createContext<StudioNavigationControls | null>(null);

/** Use the dashboard rail on desktop and its modal navigation on smaller screens. */
export default function StudioNavigation({ children }: StudioNavigationProps) {
  const parent = useContext(StudioNavigationContext);
  // Route-loading screens also work alone. Inside a workspace, reuse its
  // controls rather than mounting another rail, drawer and layout inset.
  return parent ? <>{children(parent)}</> : <StudioNavigationShell>{children}</StudioNavigationShell>;
}

function StudioNavigationShell({ children }: StudioNavigationProps) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { navigationOpen, setNavigationOpen } = useDashboardNavigationPreference();
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => setMobileOpen(false), [desktop]);
  const closeNavigation = useCallback(() => {
    setNavigationOpen(false);
    setMobileOpen(false);
  }, [setNavigationOpen]);
  const width = desktop
    ? navigationOpen
      ? DASHBOARD_NAV_EXPANDED_WIDTH
      : DASHBOARD_NAV_COLLAPSED_WIDTH
    : 0;
  const controls: StudioNavigationControls = {
    onOpenNavigation: desktop ? undefined : () => setMobileOpen(true),
    closeNavigation,
    navigationOpen: desktop ? navigationOpen : mobileOpen,
  };

  return (
    <StudioNavigationContext.Provider value={controls}>
      <Box sx={{ pr: `${width}px`, transition: "padding-right 0.22s ease" }}>
        {children(controls)}
      </Box>
      {desktop && (
        <DashboardNavigationRail expanded={navigationOpen}>
          <AccountNavigation
            expanded={navigationOpen}
            onToggleExpanded={setNavigationOpen}
          />
        </DashboardNavigationRail>
      )}
      <DashboardNavigationDrawer
        open={!desktop && mobileOpen}
        onClose={() => setMobileOpen(false)}
      >
        <AccountNavigation
          expanded
          mobile
          onClose={() => setMobileOpen(false)}
        />
      </DashboardNavigationDrawer>
    </StudioNavigationContext.Provider>
  );
}
